import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BopExcelTable — Tabella dati con sort + filtri per colonna + paginazione
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Componente generico riutilizzabile per pagine lista del gestionale.
 * Pattern UX:
 *  - Click sull'header colonna → cicla sort (asc → desc → nessuno)
 *  - Click sull'icona filtro (a destra del nome) → apre popover di filtro
 *  - Filtro live: si applica mentre digiti / spunti
 *  - Indicatore visivo (icona filtro blu) quando colonna ha filtro attivo
 *  - Click su una riga: callback onRowClick (es. naviga al dettaglio)
 *  - Paginazione client-side con selettore pagine + "X-Y di Z"
 *  - Reset chiaramente visibile quando ci sono filtri / sort attivi
 *
 * Tutti gli stili vivono in bop-design-system.css sotto il prefisso `bop-extable`.
 */

// ── Tipi pubblici ─────────────────────────────────────────────────────────────

export type BopExcelSortDir = 'asc' | 'desc' | null;

export type BopExcelColumnFilter =
  | { type: 'text' }
  | { type: 'enum'; options: Array<{ value: string; label: string }> }
  | { type: 'number' }
  | { type: 'dateRange' };

export interface BopExcelColumn<T> {
  /** Identificatore univoco della colonna (usato per stato sort/filter). */
  key: string;
  /** Etichetta header. */
  header: React.ReactNode;
  /** Valore "grezzo" usato per sort / filtri. null/undefined → vuoto. */
  accessor: (row: T) => string | number | null | undefined;
  /** Rendering della cella. Se omesso, usa accessor formattato come stringa. */
  render?: (row: T) => React.ReactNode;
  /** Abilita sort sulla colonna. Default true. */
  sortable?: boolean;
  /** Configurazione del filtro per colonna. Se assente, niente filtro. */
  filter?: BopExcelColumnFilter;
  /** Classe CSS extra sulla cella td. */
  cellClassName?: string;
  /** Classe CSS extra sull'header th. */
  headerClassName?: string;
  /** Hint di larghezza (es. "120px"). */
  width?: string;
  /** Se true, blocca propagation del click cella (utile per colonne con link/azioni). */
  stopRowClick?: boolean;
  /** Se false, la colonna non può essere nascosta dal menu "Colonne" (sempre visibile). Default true. */
  hideable?: boolean;
  /** Se true, la colonna parte nascosta (l'utente può riattivarla dal menu "Colonne"). */
  defaultHidden?: boolean;
}

export interface BopExcelTableProps<T> {
  data: T[];
  columns: BopExcelColumn<T>[];
  rowKey: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  /** Renderer azioni riga (ultima colonna a destra, non filtrabile né ordinabile). */
  rowActions?: (row: T) => React.ReactNode;
  /** Etichetta della colonna azioni (default: "Azioni"). */
  rowActionsHeader?: React.ReactNode;
  /** Stato vuoto custom (mostrato se data è vuoto E nessun filtro attivo). */
  emptyState?: React.ReactNode;
  loading?: boolean;
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  /** Default sort iniziale. */
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  /** Se true, non mostra il footer di paginazione (utile quando il componente
   *  è usato con paginazione server-side gestita esternamente). */
  hidePagination?: boolean;
  /** Se valorizzato, abilita il menu "Colonne" (mostra/nascondi) e persiste la
   *  scelta dell'utente in localStorage sotto questa chiave. */
  storageKey?: string;
  /** Se true, le colonne si ridimensionano trascinando il bordo destro della
   *  testata (doppio click sul bordo = larghezze automatiche). Con storageKey
   *  le larghezze scelte restano salvate in localStorage. */
  resizableColumns?: boolean;
}

const ACTIONS_COL_KEY = '__actions';
const MIN_COL_WIDTH = 50;

// ── Stato interno ─────────────────────────────────────────────────────────────

type EnumFilter = { selected: string[] };
type TextFilter = { value: string };
type NumberFilter = { min: string; max: string };
type DateRangeFilter = { from: string; to: string };
type AnyFilter = EnumFilter | TextFilter | NumberFilter | DateRangeFilter;

interface FilterStateMap {
  [columnKey: string]: AnyFilter;
}

// ── Icone ─────────────────────────────────────────────────────────────────────

const IconFilter = ({ active }: { active?: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const IconSortAsc = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 6l-8 12h16z" /></svg>
);
const IconSortDesc = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 18l8-12H4z" /></svg>
);
const IconSortNone = () => (
  <svg width="10" height="14" viewBox="0 0 10 14" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M5 1 L1 5 M5 1 L9 5" /><path d="M5 13 L1 9 M5 13 L9 9" />
  </svg>
);

const IconChevronLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const IconChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const IconChevronsLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="11 17 6 12 11 7" /><polyline points="18 17 13 12 18 7" />
  </svg>
);
const IconChevronsRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="13 17 18 12 13 7" /><polyline points="6 17 11 12 6 7" />
  </svg>
);
const IconClose = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IconColumns = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" />
  </svg>
);

// ── Utilities ─────────────────────────────────────────────────────────────────

function compareValues(a: unknown, b: unknown): number {
  const isNullish = (v: unknown) => v === null || v === undefined || v === '';
  if (isNullish(a) && isNullish(b)) return 0;
  if (isNullish(a)) return 1; // vuoti in fondo
  if (isNullish(b)) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const aStr = String(a);
  const bStr = String(b);
  // Tenta numerico se entrambi parseable
  const an = Number(aStr.replace(',', '.'));
  const bn = Number(bStr.replace(',', '.'));
  if (!isNaN(an) && !isNaN(bn) && aStr !== '' && bStr !== '') return an - bn;
  return aStr.localeCompare(bStr, 'it', { sensitivity: 'base', numeric: true });
}

function isFilterActive(f: AnyFilter | undefined): boolean {
  if (!f) return false;
  if ('value' in f) return f.value.trim().length > 0;
  if ('selected' in f) return f.selected.length > 0;
  if ('min' in f && 'max' in f) return f.min.trim() !== '' || f.max.trim() !== '';
  if ('from' in f && 'to' in f) return f.from.trim() !== '' || f.to.trim() !== '';
  return false;
}

// ── Popover di filtro ─────────────────────────────────────────────────────────

interface FilterPopoverProps {
  anchorRect: DOMRect;
  column: BopExcelColumn<unknown>;
  value: AnyFilter | undefined;
  onChange: (next: AnyFilter | undefined) => void;
  onClose: () => void;
}

function FilterPopover({ anchorRect, column, value, onChange, onClose }: FilterPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Posizione: sotto l'header, allineata all'icona. Se sfora viewport, sposta.
  useLayoutEffect(() => {
    const el = popoverRef.current;
    if (!el) return;
    const w = el.offsetWidth || 280;
    const h = el.offsetHeight || 200;
    let left = anchorRect.left;
    let top = anchorRect.bottom + 4;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (left + w > vw - 8) left = Math.max(8, vw - w - 8);
    if (top + h > vh - 8) top = Math.max(8, anchorRect.top - h - 4);
    setPos({ top, left });
  }, [anchorRect]);

  // Click outside dismisses
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const el = popoverRef.current;
      if (!el) return;
      if (!el.contains(e.target as Node)) onClose();
    };
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // ritarda l'attach al prossimo tick per evitare di catturare il click che ha aperto il popover
    const t = setTimeout(() => {
      document.addEventListener('mousedown', handler);
      document.addEventListener('keydown', escHandler);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', escHandler);
    };
  }, [onClose]);

  const filterCfg = column.filter!;

  return (
    <div
      ref={popoverRef}
      className="bop-extable__popover"
      role="dialog"
      aria-label={`Filtro colonna ${typeof column.header === 'string' ? column.header : column.key}`}
      style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
    >
      <div className="bop-extable__popover-header">
        <span className="bop-extable__popover-title">
          Filtra: {typeof column.header === 'string' ? column.header : column.key}
        </span>
        <button
          type="button"
          className="bop-extable__popover-close"
          onClick={onClose}
          aria-label="Chiudi"
        >
          <IconClose />
        </button>
      </div>

      <div className="bop-extable__popover-body">
        {filterCfg.type === 'text' && (
          <TextFilterBody
            value={(value as TextFilter | undefined)?.value || ''}
            onChange={(v) => onChange(v ? { value: v } : undefined)}
          />
        )}
        {filterCfg.type === 'enum' && (
          <EnumFilterBody
            options={filterCfg.options}
            selected={(value as EnumFilter | undefined)?.selected || []}
            onChange={(sel) => onChange(sel.length ? { selected: sel } : undefined)}
          />
        )}
        {filterCfg.type === 'number' && (
          <NumberFilterBody
            min={(value as NumberFilter | undefined)?.min || ''}
            max={(value as NumberFilter | undefined)?.max || ''}
            onChange={(min, max) => {
              const f: NumberFilter = { min, max };
              onChange(isFilterActive(f) ? f : undefined);
            }}
          />
        )}
        {filterCfg.type === 'dateRange' && (
          <DateRangeFilterBody
            from={(value as DateRangeFilter | undefined)?.from || ''}
            to={(value as DateRangeFilter | undefined)?.to || ''}
            onChange={(from, to) => {
              const f: DateRangeFilter = { from, to };
              onChange(isFilterActive(f) ? f : undefined);
            }}
          />
        )}
      </div>

      <div className="bop-extable__popover-footer">
        <button
          type="button"
          className="bop-extable__popover-clear"
          onClick={() => { onChange(undefined); }}
          disabled={!isFilterActive(value)}
        >
          Pulisci filtro
        </button>
        <button
          type="button"
          className="bop-extable__popover-done"
          onClick={onClose}
        >
          Fatto
        </button>
      </div>
    </div>
  );
}

// ── Bodies dei vari filtri ────────────────────────────────────────────────────

function TextFilterBody({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  return (
    <div className="bop-extable__filter-field">
      <input
        ref={inputRef}
        type="text"
        className="bop-extable__filter-input"
        placeholder="Contiene…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <p className="bop-extable__filter-hint">Mostra righe che contengono il testo (case-insensitive).</p>
    </div>
  );
}

function EnumFilterBody({
  options,
  selected,
  onChange,
}: {
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (sel: string[]) => void;
}) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(
    () => options.filter(o => o.label.toLowerCase().includes(search.toLowerCase())),
    [options, search]
  );
  const allSelected = selected.length === options.length || selected.length === 0;
  const toggle = (val: string) => {
    if (selected.includes(val)) {
      onChange(selected.filter(s => s !== val));
    } else {
      onChange([...selected, val]);
    }
  };
  const setAll = () => {
    onChange(allSelected ? [] : options.map(o => o.value));
  };
  return (
    <div className="bop-extable__filter-field">
      {options.length > 6 && (
        <input
          type="text"
          className="bop-extable__filter-input"
          placeholder="Cerca opzione…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <label className="bop-extable__filter-check bop-extable__filter-check--all">
        <input
          type="checkbox"
          checked={selected.length === 0}
          onChange={setAll}
        />
        <span>(Tutti)</span>
      </label>
      <div className="bop-extable__filter-checks">
        {filtered.length === 0 && (
          <p className="bop-extable__filter-hint">Nessuna opzione corrisponde.</p>
        )}
        {filtered.map(opt => {
          const checked = selected.length === 0 || selected.includes(opt.value);
          return (
            <label key={opt.value} className="bop-extable__filter-check">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => {
                  // Se "tutti" attivo, trasformiamo in selezione esplicita meno questo
                  if (selected.length === 0) {
                    onChange(options.filter(o => o.value !== opt.value).map(o => o.value));
                  } else {
                    toggle(opt.value);
                  }
                }}
              />
              <span>{opt.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function NumberFilterBody({
  min,
  max,
  onChange,
}: {
  min: string;
  max: string;
  onChange: (min: string, max: string) => void;
}) {
  return (
    <div className="bop-extable__filter-field">
      <div className="bop-extable__filter-range">
        <div className="bop-extable__filter-range-col">
          <label className="bop-extable__filter-label">Da</label>
          <input
            type="number"
            className="bop-extable__filter-input"
            placeholder="min"
            value={min}
            onChange={(e) => onChange(e.target.value, max)}
          />
        </div>
        <div className="bop-extable__filter-range-col">
          <label className="bop-extable__filter-label">A</label>
          <input
            type="number"
            className="bop-extable__filter-input"
            placeholder="max"
            value={max}
            onChange={(e) => onChange(min, e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

function DateRangeFilterBody({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}) {
  return (
    <div className="bop-extable__filter-field">
      <div className="bop-extable__filter-range">
        <div className="bop-extable__filter-range-col">
          <label className="bop-extable__filter-label">Da</label>
          <input
            type="date"
            className="bop-extable__filter-input"
            value={from}
            onChange={(e) => onChange(e.target.value, to)}
          />
        </div>
        <div className="bop-extable__filter-range-col">
          <label className="bop-extable__filter-label">A</label>
          <input
            type="date"
            className="bop-extable__filter-input"
            value={to}
            onChange={(e) => onChange(from, e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

// ── Funzione di matching filtro ───────────────────────────────────────────────

function rowMatchesFilter<T>(
  row: T,
  column: BopExcelColumn<T>,
  filter: AnyFilter
): boolean {
  if (!column.filter) return true;
  const rawValue = column.accessor(row);
  switch (column.filter.type) {
    case 'text': {
      const v = (filter as TextFilter).value.trim().toLowerCase();
      if (!v) return true;
      if (rawValue === null || rawValue === undefined) return false;
      return String(rawValue).toLowerCase().includes(v);
    }
    case 'enum': {
      const sel = (filter as EnumFilter).selected;
      if (sel.length === 0) return true;
      if (rawValue === null || rawValue === undefined) return false;
      return sel.includes(String(rawValue));
    }
    case 'number': {
      const f = filter as NumberFilter;
      const minOk = f.min === '' || (rawValue !== null && rawValue !== undefined && Number(rawValue) >= Number(f.min));
      const maxOk = f.max === '' || (rawValue !== null && rawValue !== undefined && Number(rawValue) <= Number(f.max));
      return minOk && maxOk;
    }
    case 'dateRange': {
      const f = filter as DateRangeFilter;
      if (rawValue === null || rawValue === undefined || rawValue === '') {
        return f.from === '' && f.to === '';
      }
      const d = String(rawValue).slice(0, 10); // YYYY-MM-DD
      const fromOk = f.from === '' || d >= f.from;
      const toOk = f.to === '' || d <= f.to;
      return fromOk && toOk;
    }
  }
}

// ── Menu "Colonne" (mostra/nascondi) ──────────────────────────────────────────

interface ColumnsMenuProps<T> {
  columns: BopExcelColumn<T>[];
  hiddenCols: Set<string>;
  onToggle: (key: string) => void;
  onSetAll: (allVisible: boolean) => void;
}

function ColumnsMenu<T>({ columns, hiddenCols, onToggle, onSetAll }: ColumnsMenuProps<T>) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const escHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const t = setTimeout(() => {
      document.addEventListener('mousedown', handler);
      document.addEventListener('keydown', escHandler);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', escHandler);
    };
  }, [open]);

  const hideableCols = columns.filter(c => c.hideable !== false);
  const visibleCount = columns.length - hiddenCols.size;
  const allVisible = hideableCols.every(c => !hiddenCols.has(c.key));

  return (
    <div className="bop-extable__cols" ref={wrapRef}>
      <button
        type="button"
        className={`bop-extable__cols-btn ${open ? 'bop-extable__cols-btn--open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        title="Mostra/nascondi colonne"
      >
        <IconColumns />
        <span>Colonne</span>
        <span className="bop-extable__cols-count">{visibleCount}/{columns.length}</span>
      </button>
      {open && (
        <div className="bop-extable__cols-menu" role="menu">
          <div className="bop-extable__cols-menu-header">
            <span className="bop-extable__cols-menu-title">Colonne visibili</span>
            <button
              type="button"
              className="bop-extable__cols-menu-all"
              onClick={() => onSetAll(!allVisible)}
            >
              {allVisible ? 'Nascondi tutte' : 'Mostra tutte'}
            </button>
          </div>
          <div className="bop-extable__cols-menu-list">
            {columns.map(col => {
              const locked = col.hideable === false;
              const visible = !hiddenCols.has(col.key);
              return (
                <label
                  key={col.key}
                  className={`bop-extable__cols-item ${locked ? 'bop-extable__cols-item--locked' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={visible}
                    disabled={locked}
                    onChange={() => onToggle(col.key)}
                  />
                  <span>{typeof col.header === 'string' ? col.header : col.key}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Componente principale ─────────────────────────────────────────────────────

export default function BopExcelTable<T>({
  data,
  columns,
  rowKey,
  onRowClick,
  rowActions,
  rowActionsHeader = 'Azioni',
  emptyState,
  loading,
  pageSizeOptions = [25, 50, 100, 200],
  defaultPageSize = 50,
  initialSort,
  hidePagination = false,
  storageKey,
  resizableColumns = false,
}: BopExcelTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(initialSort?.key ?? null);
  const [sortDir, setSortDir] = useState<BopExcelSortDir>(initialSort?.dir ?? null);
  const [filters, setFilters] = useState<FilterStateMap>({});
  const [openFilterCol, setOpenFilterCol] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // ── Visibilità colonne (menu "Colonne", persistita se storageKey valorizzato) ──
  const columnsToggleEnabled = !!storageKey;
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(() => {
    if (storageKey) {
      try {
        const raw = window.localStorage.getItem(`bop-extable-cols:${storageKey}`);
        if (raw) {
          const arr = JSON.parse(raw) as string[];
          if (Array.isArray(arr)) return new Set(arr);
        }
      } catch { /* ignora localStorage non disponibile / JSON invalido */ }
    }
    // Default: colonne marcate defaultHidden (ma mai quelle non nascondibili)
    return new Set(columns.filter(c => c.defaultHidden && c.hideable !== false).map(c => c.key));
  });

  // Persiste la scelta dell'utente
  useEffect(() => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(`bop-extable-cols:${storageKey}`, JSON.stringify(Array.from(hiddenCols)));
    } catch { /* ignora */ }
  }, [storageKey, hiddenCols]);

  const visibleColumns = useMemo(
    () => (columnsToggleEnabled ? columns.filter(c => !hiddenCols.has(c.key)) : columns),
    [columns, hiddenCols, columnsToggleEnabled]
  );

  const toggleCol = useCallback((key: string) => {
    setHiddenCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const setAllCols = useCallback((allVisible: boolean) => {
    setHiddenCols(() => {
      if (allVisible) return new Set<string>();
      return new Set(columns.filter(c => c.hideable !== false).map(c => c.key));
    });
  }, [columns]);

  // ── Larghezze colonne (ridimensionamento da testata) ──
  // null = layout automatico. Al primo trascinamento si misurano tutte le
  // colonne e la tabella passa a layout fisso con larghezze esplicite: con il
  // layout automatico il browser ignorerebbe una larghezza sotto il contenuto.
  const [colWidths, setColWidths] = useState<Record<string, number> | null>(() => {
    if (!resizableColumns || !storageKey) return null;
    try {
      const raw = window.localStorage.getItem(`bop-extable-widths:${storageKey}`);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === 'object') return obj as Record<string, number>;
      }
    } catch { /* ignora localStorage non disponibile / JSON invalido */ }
    return null;
  });
  const [resizing, setResizing] = useState(false);
  const thRefs = useRef<Record<string, HTMLTableCellElement | null>>({});

  useEffect(() => {
    if (!resizableColumns || !storageKey) return;
    try {
      const k = `bop-extable-widths:${storageKey}`;
      if (colWidths) window.localStorage.setItem(k, JSON.stringify(colWidths));
      else window.localStorage.removeItem(k);
    } catch { /* ignora */ }
  }, [resizableColumns, storageKey, colWidths]);

  const startResize = (e: React.PointerEvent, key: string) => {
    e.preventDefault();
    e.stopPropagation();
    const base: Record<string, number> = { ...(colWidths || {}) };
    for (const [k, th] of Object.entries(thRefs.current)) {
      if (th) base[k] = Math.round(th.getBoundingClientRect().width);
    }
    const startX = e.clientX;
    const startW = base[key] ?? MIN_COL_WIDTH;
    setColWidths(base);
    setResizing(true);
    const onMove = (ev: PointerEvent) => {
      const w = Math.max(MIN_COL_WIDTH, Math.round(startW + ev.clientX - startX));
      setColWidths(prev => ({ ...(prev || base), [key]: w }));
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      setResizing(false);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  };

  const resizeHandle = (key: string) => resizableColumns ? (
    <span
      className="bop-extable__resize"
      role="separator"
      aria-orientation="vertical"
      title="Trascina per ridimensionare — doppio click per le larghezze automatiche"
      onPointerDown={(e) => startResize(e, key)}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => { e.stopPropagation(); setColWidths(null); }}
    />
  ) : null;

  const filterButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [popoverAnchor, setPopoverAnchor] = useState<DOMRect | null>(null);

  // Reset pagina quando cambiano data/filtri/sort/pageSize
  useEffect(() => { setPage(1); }, [data, filters, sortKey, sortDir, pageSize]);

  // Riposiziona popover al resize
  useEffect(() => {
    if (!openFilterCol) return;
    const handler = () => {
      const btn = filterButtonRefs.current[openFilterCol];
      if (btn) setPopoverAnchor(btn.getBoundingClientRect());
    };
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [openFilterCol]);

  // Apply filters → sort → paginate
  const processed = useMemo(() => {
    let rows = data;
    // Filter
    for (const col of columns) {
      const f = filters[col.key];
      if (col.filter && f && isFilterActive(f)) {
        rows = rows.filter(r => rowMatchesFilter(r, col, f));
      }
    }
    // Sort
    if (sortKey && sortDir) {
      const col = columns.find(c => c.key === sortKey);
      if (col) {
        const sign = sortDir === 'asc' ? 1 : -1;
        rows = [...rows].sort((a, b) => sign * compareValues(col.accessor(a), col.accessor(b)));
      }
    }
    return rows;
  }, [data, columns, filters, sortKey, sortDir]);

  const totalFiltered = processed.length;
  const totalUnfiltered = data.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(
    () => processed.slice((safePage - 1) * pageSize, safePage * pageSize),
    [processed, safePage, pageSize]
  );

  const anyFilterActive = Object.values(filters).some(f => isFilterActive(f));
  const anySortActive = sortKey !== null && sortDir !== null;
  const anyActive = anyFilterActive || anySortActive;

  const resetAll = useCallback(() => {
    setFilters({});
    setSortKey(null);
    setSortDir(null);
  }, []);

  const cycleSort = (key: string) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    if (sortDir === 'desc') { setSortKey(null); setSortDir(null); return; }
    setSortDir('asc');
  };

  const openFilter = (key: string) => {
    const btn = filterButtonRefs.current[key];
    if (!btn) return;
    setPopoverAnchor(btn.getBoundingClientRect());
    setOpenFilterCol(key);
  };

  const closeFilter = () => {
    setOpenFilterCol(null);
    setPopoverAnchor(null);
  };

  const setColFilter = (key: string, next: AnyFilter | undefined) => {
    setFilters(prev => {
      const copy = { ...prev };
      if (next === undefined) delete copy[key];
      else copy[key] = next;
      return copy;
    });
  };

  const totalCols = visibleColumns.length + (rowActions ? 1 : 0);
  const fixedWidths = resizableColumns && colWidths !== null;
  const widthOf = (key: string) => colWidths?.[key] ?? 120;
  const fixedTableWidth = fixedWidths
    ? visibleColumns.reduce((sum, c) => sum + widthOf(c.key), 0) + (rowActions ? widthOf(ACTIONS_COL_KEY) : 0)
    : undefined;
  const showEmpty = !loading && totalFiltered === 0;
  const isEmptyOriginal = !loading && totalUnfiltered === 0;

  return (
    <div className={`bop-extable ${resizing ? 'bop-extable--resizing' : ''}`.trim()}>
      {/* Riga di stato sopra la tabella: conteggio + reset */}
      <div className="bop-extable__topbar">
        <div className="bop-extable__count">
          {loading ? (
            <span className="bop-extable__count-text">Caricamento…</span>
          ) : totalFiltered === totalUnfiltered ? (
            <span className="bop-extable__count-text">
              {totalUnfiltered === 0 ? 'Nessun risultato' : `${totalUnfiltered} risultati`}
            </span>
          ) : (
            <span className="bop-extable__count-text">
              <strong>{totalFiltered}</strong> di {totalUnfiltered} risultati
            </span>
          )}
        </div>
        <div className="bop-extable__topbar-actions">
          {anyActive && (
            <button
              type="button"
              className="bop-extable__reset"
              onClick={resetAll}
            >
              <IconClose /> Reimposta ordinamento e filtri
            </button>
          )}
          {columnsToggleEnabled && (
            <ColumnsMenu
              columns={columns}
              hiddenCols={hiddenCols}
              onToggle={toggleCol}
              onSetAll={setAllCols}
            />
          )}
        </div>
      </div>

      <div className="bop-extable__scroll">
        {/* Larghezze in style: sono calcolate a runtime dal trascinamento. */}
        <table
          className={`bop-extable__table ${fixedWidths ? 'bop-extable__table--fixed' : ''}`.trim()}
          style={fixedTableWidth !== undefined ? { width: fixedTableWidth } : undefined}
        >
          <colgroup>
            {visibleColumns.map(c => (
              <col
                key={c.key}
                style={fixedWidths ? { width: widthOf(c.key) } : c.width ? { width: c.width } : undefined}
              />
            ))}
            {rowActions && <col style={{ width: fixedWidths ? widthOf(ACTIONS_COL_KEY) : '1%' }} />}
          </colgroup>
          <thead>
            <tr>
              {visibleColumns.map(col => {
                const isSortable = col.sortable !== false;
                const isFilterable = !!col.filter;
                const isSortActive = sortKey === col.key && sortDir !== null;
                const isFilterActiveCol = isFilterActive(filters[col.key]);
                return (
                  <th
                    key={col.key}
                    ref={(el) => { thRefs.current[col.key] = el; }}
                    className={`bop-extable__th ${col.headerClassName || ''}`.trim()}
                    aria-sort={
                      !isSortActive ? 'none' :
                      sortDir === 'asc' ? 'ascending' : 'descending'
                    }
                  >
                    <div className="bop-extable__th-inner">
                      <button
                        type="button"
                        className={`bop-extable__th-label ${isSortable ? 'bop-extable__th-label--sortable' : ''}`}
                        onClick={isSortable ? () => cycleSort(col.key) : undefined}
                        disabled={!isSortable}
                        title={isSortable ? 'Click per ordinare' : undefined}
                      >
                        <span className="bop-extable__th-text">{col.header}</span>
                        {isSortable && (
                          <span className={`bop-extable__sort-icon ${isSortActive ? 'bop-extable__sort-icon--active' : ''}`}>
                            {isSortActive
                              ? (sortDir === 'asc' ? <IconSortAsc /> : <IconSortDesc />)
                              : <IconSortNone />}
                          </span>
                        )}
                      </button>
                      {isFilterable && (
                        <button
                          type="button"
                          ref={(el) => { filterButtonRefs.current[col.key] = el; }}
                          className={`bop-extable__filter-btn ${isFilterActiveCol ? 'bop-extable__filter-btn--active' : ''} ${openFilterCol === col.key ? 'bop-extable__filter-btn--open' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (openFilterCol === col.key) closeFilter();
                            else openFilter(col.key);
                          }}
                          aria-label={isFilterActiveCol ? 'Filtro attivo: modifica' : 'Filtra colonna'}
                          aria-pressed={isFilterActiveCol}
                          title={isFilterActiveCol ? 'Filtro attivo — click per modificare' : 'Filtra colonna'}
                        >
                          <IconFilter active={isFilterActiveCol} />
                        </button>
                      )}
                    </div>
                    {resizeHandle(col.key)}
                  </th>
                );
              })}
              {rowActions && (
                <th
                  ref={(el) => { thRefs.current[ACTIONS_COL_KEY] = el; }}
                  className="bop-extable__th bop-extable__th--actions"
                >
                  <span className="bop-extable__th-text">{rowActionsHeader}</span>
                  {resizeHandle(ACTIONS_COL_KEY)}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={totalCols} className="bop-extable__state-cell">
                  <div className="bop-extable__loader">Caricamento…</div>
                </td>
              </tr>
            )}
            {!loading && showEmpty && (
              <tr>
                <td colSpan={totalCols} className="bop-extable__state-cell">
                  {isEmptyOriginal ? (
                    (emptyState ?? (
                      <div className="bop-extable__empty">
                        <p>Nessun risultato.</p>
                      </div>
                    ))
                  ) : (
                    <div className="bop-extable__empty">
                      <p>Nessun risultato corrisponde ai filtri attivi.</p>
                      <button
                        type="button"
                        className="bop-extable__empty-reset"
                        onClick={resetAll}
                      >
                        Reimposta filtri
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            )}
            {!loading && !showEmpty && paged.map(row => (
              <tr
                key={rowKey(row)}
                className={onRowClick ? 'bop-extable__row bop-extable__row--clickable' : 'bop-extable__row'}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {visibleColumns.map(col => (
                  <td
                    key={col.key}
                    className={`bop-extable__td ${col.cellClassName || ''}`.trim()}
                    onClick={col.stopRowClick ? (e) => e.stopPropagation() : undefined}
                  >
                    {col.render ? col.render(row) : (() => {
                      const v = col.accessor(row);
                      return v === null || v === undefined || v === '' ? <span className="bop-extable__cell-empty">—</span> : String(v);
                    })()}
                  </td>
                ))}
                {rowActions && (
                  <td
                    className="bop-extable__td bop-extable__td--actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {rowActions(row)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer paginazione */}
      {!hidePagination && !loading && totalFiltered > 0 && (
        <div className="bop-extable__pager">
          <div className="bop-extable__pager-info">
            {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, totalFiltered)} di {totalFiltered}
          </div>
          <div className="bop-extable__pager-controls">
            <label className="bop-extable__pager-size">
              <span>Righe per pagina</span>
              <select
                className="bop-extable__pager-select"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {pageSizeOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
            <div className="bop-extable__pager-nav">
              <button
                type="button"
                className="bop-extable__pager-btn"
                onClick={() => setPage(1)}
                disabled={safePage === 1}
                aria-label="Prima pagina"
              ><IconChevronsLeft /></button>
              <button
                type="button"
                className="bop-extable__pager-btn"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={safePage === 1}
                aria-label="Pagina precedente"
              ><IconChevronLeft /></button>
              <span className="bop-extable__pager-current">
                Pag. {safePage} / {totalPages}
              </span>
              <button
                type="button"
                className="bop-extable__pager-btn"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                aria-label="Pagina successiva"
              ><IconChevronRight /></button>
              <button
                type="button"
                className="bop-extable__pager-btn"
                onClick={() => setPage(totalPages)}
                disabled={safePage === totalPages}
                aria-label="Ultima pagina"
              ><IconChevronsRight /></button>
            </div>
          </div>
        </div>
      )}

      {/* Popover filtro */}
      {openFilterCol && popoverAnchor && (() => {
        const col = columns.find(c => c.key === openFilterCol);
        if (!col || !col.filter) return null;
        return (
          <FilterPopover
            anchorRect={popoverAnchor}
            column={col as BopExcelColumn<unknown>}
            value={filters[openFilterCol]}
            onChange={(next) => setColFilter(openFilterCol, next)}
            onClose={closeFilter}
          />
        );
      })()}
    </div>
  );
}
